<!--
 * @Author: rossdong rossdong@yntengyun.com
 * @Date: 2026-03-18
 * @LastEditors: rossdong rossdong@yntengyun.com
 * @LastEditTime: 2026-05-07 14:50:16
 * @FilePath: /Gov-Web/Mobile/src/pages/complaint/views/visual/disposalAnalysis/comps/FirstPointResponseAvegerCell.vue
 * @Description: 处置情况分析-首次响应平均时长
 *
-->
<template>
  <div class="w-full flex flex-col">
    <div class="w-full flex items-end justify-between my-16">
      <div class="text-16 font-bold text-black-primary">平均响应时长</div>
      <div class="flex items-center" @click="ruleDialogShow = true">
        <img src="@/assets/complaint/ic_info_tips.png" alt="" class="size-14 mr-3">
        <span>统计说明</span>
      </div>
    </div>

    <div class="card" v-if="!tableMode">
      <div class="card-head">
        <div class="unit-text">单位（小时）</div>

      </div>

      <div v-if="loading" class="state-text">
        加载中...
      </div>
      <template v-else>
        <div v-if="!list.length" class="state-text">暂无数据</div>
        <div v-else ref="chartEl" class="chart"></div>

        <div class="w-full flex justify-center">
          <div class="avg-tag">平均值：{{ avgTimeStr || "-" }}</div>
        </div>
      </template>
    </div>

    <div v-else class="card flex flex-col px-10 pt-10 pb-20">
      <o-table :columns="tableColumns" :data="tableData" :height="330" />
    </div>

    <div class="w-full flex justify-center items-center mb-13" @click="tableMode = !tableMode">
      <img src="@/assets/complaint/ic_exchange.png" class="size-12 mr-4">
      <span v-if="tableMode" class="text-[#229F60]">切换为图表</span>
      <span v-else class="text-[#229F60]">切换为表格</span>
    </div>

    <TipsDialog v-model="ruleDialogShow" title="统计说明" :content="ruleText" confirmButtonText="关闭" />
  </div>
</template>

<script>
import echarts from "echarts";
import APIS from "@/service/modules/complaintApis/modules/visualization.js";
import { Table } from "@/components/Table";
import TipsDialog from "../../comps/TipsDialog.vue";
import visualFilterMixin from "../../mixins/visualFilterMixin";

function secToHour(sec) {
  const n = Number(sec);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Number((n / 3600).toFixed(2));
}

export default {
  name: "FirstPointResponseAvegerCell",
  mixins: [visualFilterMixin],
  components: {
    "o-table": Table,
    TipsDialog,
  },
  props: {
    county: { type: String, default: "" }
  },
  data() {
    return {
      chart: null,
      _onResize: null,

      loading: false,
      tableMode: false,
      ruleDialogShow: false,

      total: 0,
      avgTime: 0,
      avgTimeStr: "",
      list: [],
      tableColumns: [
        { title: "排序", dataProp: "sort", minWidth: "60px", fixed: "left" },
        { title: "区域", dataProp: "cityName", minWidth: "100px", fixed: "left" },
        { title: "已响应投诉量", dataProp: "normalTotal", minWidth: "110px" ,
          renderCell(h, { rowData }) {
            return h("div", `${rowData.total}件`);
          }
        },
        { title: "超5分钟响应量", dataProp: "moreThanTotal", minWidth: "110px" ,
          renderCell(h, { rowData }) {
            return h("div", `${rowData.moreThanTotal}件`);
          }
        },
        { title: "5分钟响应率", dataProp: "normalRatio", minWidth: "110px" },
        { title: "平均响应时长", dataProp: "avgTimeStr", minWidth: "160px" },
      ],
      ruleText:
        `1.仅统计第一落点指挥中心“已响应”的投诉件；
2.“游客撤销”的投诉件：省级投诉专班转办前游客撤销，因指挥中心未进行响应，不计入统计；指挥中心响应后游客再撤销，需计入统计；
3.按投诉件第一落点指挥中心所属区域统计。例子：投诉件第一落点为大理州-大理市，投诉件上报后重新分发到昆明市-官渡区办结，则统计投诉件响应时长时，该件应计入大理州-大理市，不计入昆明市-官渡区。`
    };
  },
  computed: {
    tableData() {
      return (this.list || []).map((item, idx) => ({
        ...item,
        sort: idx + 1
      }));
    }
  },
  watch: {
    list: {
      deep: true,
      handler() {
        this.$nextTick(() => this.renderChart());
      }
    },
    tableMode(val) {
      if (val) {
        this.disposeChart();
        return;
      }
      this.$nextTick(() => {
        this.ensureChart();
        this.renderChart();
      });
    }
  },
  methods: {
    ensureChart() {
      const el = this.$refs.chartEl;
      if (!el) return;
      if (this.chart) {
        if (typeof this.chart.getDom === "function" && this.chart.getDom() !== el) {
          this.disposeChart();
        } else {
          return;
        }
      }
      this.chart = echarts.init(el);
      this._onResize = () => {
        if (this.chart) this.chart.resize();
      };
      window.addEventListener("resize", this._onResize, { passive: true });
    },
    disposeChart() {
      if (this._onResize) {
        window.removeEventListener("resize", this._onResize);
        this._onResize = null;
      }
      if (this.chart) {
        this.chart.dispose();
        this.chart = null;
      }
    },
    buildOption() {
      const names = (this.list || []).map(item => item.cityName || "-");
      const values = (this.list || []).map(item => secToHour(item.avgTime));
      const maxValue = values.length ? Math.max(...values) : 0;
      const yMax = maxValue > 0 ? Math.ceil(maxValue * 1.2) : 10;
      const total = names.length;
      const windowSize = 5;
      const startValue = 0;
      const endValue = total > 0 ? Math.min(windowSize - 1, total - 1) : 0;

      return {
        grid: {
          left: 40,
          right: 10,
          top: 20,
          bottom: 45,
          containLabel: false
        },
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
          extraCssText: 'z-index: 999;', // 确保层级高于图形
          z: 999,
          formatter: (params) => {
            const row = params && params[0] ? params[0] : null;
            if (!row) return "";
            const idx = row.dataIndex;
            const item = this.list[idx] || {};
            return `${item.cityName || "-"}<br/>${item.avgTimeStr || "-"}`;
          }
        },
        dataZoom: [
          {
            type: "inside",
            xAxisIndex: 0,
            zoomLock: true,
            startValue,
            endValue,
            moveOnMouseMove: true,
            moveOnMouseWheel: true,
            zoomOnMouseWheel: false
          }
        ],
        xAxis: {
          type: "category",
          data: names,
          axisTick: { show: false },
          axisLine: { lineStyle: { color: "#d9d9d9" } },
          axisLabel: {
            color: "#333",
            fontSize: 12,
            margin: 10,
            interval: 0
          }
        },
        yAxis: {
          type: "value",
          min: 0,
          max: yMax,
          splitNumber: 4,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: "#999", fontSize: 12 },
          splitLine: { lineStyle: { color: "#e5e5e5", type: "solid" } }
        },
        series: [
          {
            type: "bar",
            data: values,
            barWidth: 15,
            barCategoryGap: "70%",
            itemStyle: {
              color: "#36B374",
              borderRadius: [8, 8, 0, 0]
            }
          }
        ]
      };
    },
    renderChart() {
      if (this.loading || !this.list.length) return;
      this.$nextTick(() => {
        this.ensureChart();
        if (!this.chart) return;
        this.chart.setOption(this.buildOption(), true);
        this.chart.resize();
      });
    },
    onLoad() {
      const { areaId, beginTime, endTime, rule, channel } = this.filterParams;
      const params = {
        cityId: areaId,
        county: this.county,
        startDate: beginTime,
        endDate: endTime,
        rule: rule,
        channel: channel,
      };

      this.loading = true;
      return APIS.firstPointResponseCount(params).then((res) => {
        if (!res || res.ret !== 0) {
          this.total = 0;
          this.avgTime = 0;
          this.avgTimeStr = "";
          this.list = [];
          return;
        }
        const data = res.data || {};
        this.total = Number(data.total || 0);
        this.avgTime = Number(data.avgTime || 0);
        this.avgTimeStr = data.avgTimeStr || "";
        this.list = Array.isArray(data.list) ? data.list : [];
      }).finally(() => {
        this.loading = false;
        this.$nextTick(() => this.renderChart());
      });
    }
  },
  mounted() {
    this.ensureChart();
  },
  beforeDestroy() {
    this.disposeChart();
  }
};
</script>

<style lang="less" scoped>
.card {
  margin-bottom: 13px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.04);
  padding: 12px;
}

.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.unit-text {
  font-size: 14px;
  line-height: 20px;
  color: #999;
}

.assess-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}

.assess-label {
  font-size: 14px;
  line-height: 20px;
  color: #1E2226;
}

.chart {
  height: 320px;
  width: 100%;
}

.state-text {
  width: 100%;
  height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 14px;
}

.avg-tag {
  margin: 8px auto 2px;
  padding: 8px 22px;
  border-radius: 6px;
  background: rgba(58, 127, 240, 0.18);
  color: #2f63c7;
  font-size: 12px;
  font-weight: 400;
}
</style>
